import { useEffect, useMemo, useState } from 'react'
import type { FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { ApiError } from '@/api/client'
import { instrumentsApi, roomsApi, scheduleApi, studentsApi, teachersApi } from '@/api/endpoints'
import type { Lesson, SkippedOccurrence, Weekday } from '@/api/types'
import { useAuth } from '@/auth/AuthContext'
import { isTeacher } from '@/auth/permissions'
import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { SelectField, TextField } from '@/components/ui/Field'
import { Modal } from '@/components/ui/Modal'
import { Spinner } from '@/components/ui/Spinner'
import { TimeSelect } from '@/components/ui/TimeSelect'
import { useToast } from '@/components/ui/Toast'
import { WEEKDAY_EVERY, WEEKDAY_LABELS, WEEKDAYS } from '@/lib/dictionaries'
import { ostatniaSala, zapamietajSale } from '@/lib/ostatniaSala'
import { addDays, combineToIso, toIsoDate, toTimeInput, weekdayOf } from '@/lib/scheduleTime'

const CZASY = [
  { value: '30', label: '30 minut' },
  { value: '45', label: '45 minut' },
  { value: '60', label: '60 minut' },
  { value: '90', label: '90 minut' },
]

interface Props {
  open: boolean
  onClose: () => void
  locationId: string
  /** Wypełnia formularz przy zakładaniu z pustego slotu w siatce. */
  preset?: { day: Date; hour: number; roomId?: string } | null
  /** Edycja istniejących zajęć — wtedy cykl jest niedostępny. */
  lesson?: Lesson | null
  /**
   * Edycja całego planu tygodniowego zamiast pojedynczych zajęć.
   *
   * Zmiana przebudowuje wyłącznie przyszłe terminy — historia zostaje, bo
   * odbyte lekcje są podstawą rozliczeń.
   */
  seriesId?: string | null
  /**
   * Zajęcia, które ten termin odrabia.
   *
   * Formularz wypełnia się wtedy danymi opuszczonej lekcji, a zapis wiąże obie:
   * nieobecność znika z listy „do odrobienia”, a nowe zajęcia dostają
   * plakietkę „odrabianie”.
   */
  makeupFor?: Lesson | null
}

interface FormState {
  roomId: string
  teacherId: string
  studentId: string
  instrumentId: string
  date: string
  time: string
  duration: string
  title: string
  notes: string
  recurring: boolean
  weekday: Weekday
  endsOn: string
}

export function LessonFormModal({
  open,
  onClose,
  locationId,
  preset,
  lesson,
  seriesId,
  makeupFor,
}: Props) {
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const { notifySuccess } = useToast()
  const isEdit = Boolean(lesson)
  const isSeriesEdit = Boolean(seriesId)
  const isMakeup = Boolean(makeupFor)
  const teacherMode = isTeacher(user)

  const [form, setForm] = useState<FormState | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  const [skipped, setSkipped] = useState<SkippedOccurrence[]>([])

  const roomsQuery = useQuery({
    queryKey: ['rooms', 'picker', locationId],
    queryFn: () => roomsApi.list({ size: 200, location_id: locationId || undefined }),
    enabled: open,
  })
  const teachersQuery = useQuery({
    queryKey: ['teachers', 'picker', locationId],
    queryFn: () => teachersApi.list({ size: 200, location_id: locationId || undefined }),
    enabled: open && !teacherMode,
  })
  const studentsQuery = useQuery({
    queryKey: ['students', 'picker', locationId],
    queryFn: () => studentsApi.list({ size: 200, location_id: locationId || undefined }),
    enabled: open,
  })
  const instrumentsQuery = useQuery({
    queryKey: ['instruments'],
    queryFn: () => instrumentsApi.list(),
    enabled: open,
  })

  const seriesQuery = useQuery({
    queryKey: ['schedule', 'series', seriesId],
    queryFn: () => scheduleApi.seriesDetail(seriesId!),
    enabled: open && Boolean(seriesId),
  })

  const rooms = roomsQuery.data?.items ?? []
  const series = seriesQuery.data

  useEffect(() => {
    if (!open) return
    setError(null)
    setFieldErrors({})
    setSkipped([])

    if (seriesId) {
      // Czekamy na dane cyklu — pusty formularz podstawiłby wartości domyślne
      // i cicho nadpisał nimi ustawienia planu przy pierwszym zapisie.
      if (!series) return
      setForm({
        roomId: series.room?.id ?? '',
        teacherId: series.teacher.id,
        studentId: series.student?.id ?? '',
        instrumentId: series.instrument?.id ?? '',
        date: series.starts_on,
        time: series.start_time.slice(0, 5),
        duration: String(series.duration_minutes),
        title: series.title ?? '',
        notes: series.notes ?? '',
        recurring: true,
        weekday: series.weekday,
        endsOn: series.ends_on,
      })
      return
    }

    if (makeupFor) {
      // Odrobienie dziedziczy wszystko po opuszczonej lekcji poza terminem —
      // to ta sama nauka z tym samym uczniem, tylko innego dnia.
      setForm({
        roomId: makeupFor.room?.id ?? '',
        teacherId: makeupFor.teacher.id,
        studentId: makeupFor.student?.id ?? '',
        instrumentId: makeupFor.instrument?.id ?? '',
        date: toIsoDate(addDays(new Date(), 1)),
        time: toTimeInput(new Date(makeupFor.starts_at)),
        duration: String(makeupFor.duration_minutes),
        title: makeupFor.title ?? '',
        notes: '',
        recurring: false,
        weekday: weekdayOf(addDays(new Date(), 1)),
        endsOn: toIsoDate(addDays(new Date(), 120)),
      })
      return
    }

    if (lesson) {
      const start = new Date(lesson.starts_at)
      setForm({
        roomId: lesson.room?.id ?? '',
        teacherId: lesson.teacher.id,
        studentId: lesson.student?.id ?? '',
        instrumentId: lesson.instrument?.id ?? '',
        date: toIsoDate(start),
        time: toTimeInput(start),
        duration: String(lesson.duration_minutes),
        title: lesson.title ?? '',
        notes: lesson.notes ?? '',
        recurring: false,
        weekday: weekdayOf(start),
        endsOn: toIsoDate(addDays(start, 120)),
      })
      return
    }

    const day = preset?.day ?? new Date()
    setForm({
      // Kliknięcie w siatkę wskazuje salę wprost; poza tym podpowiadamy tę,
      // w której ta osoba wpisywała ostatnio — grafik układa się salami.
      roomId: preset?.roomId ?? ostatniaSala(locationId, rooms) ?? rooms[0]?.id ?? '',
      teacherId: teacherMode ? (user?.id ?? '') : '',
      studentId: '',
      instrumentId: '',
      date: toIsoDate(day),
      time: `${String(preset?.hour ?? 14).padStart(2, '0')}:00`,
      duration: '45',
      title: '',
      notes: '',
      recurring: false,
      weekday: weekdayOf(day),
      // Domyślny horyzont cyklu: cztery miesiące, czyli mniej więcej semestr.
      endsOn: toIsoDate(addDays(day, 120)),
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, lesson, preset, rooms.length, seriesId, series, makeupFor, locationId])

  const update = <K extends keyof FormState>(field: K, value: FormState[K]) => {
    setForm((current) => (current ? { ...current, [field]: value } : current))
  }

  // Uczniowie po nazwisku — tak szuka się w szkole, jak w dzienniku.
  // Etykieta też zaczyna się od nazwiska, inaczej lista wyglądałaby na
  // nieuporządkowaną, choć byłaby posortowana.
  const uczniowie = useMemo(
    () =>
      [...(studentsQuery.data?.items ?? [])].sort(
        (a, b) =>
          a.last_name.localeCompare(b.last_name, 'pl') ||
          a.first_name.localeCompare(b.first_name, 'pl'),
      ),
    [studentsQuery.data],
  )

  const wybranyUczen = uczniowie.find((item) => item.id === form?.studentId)

  /**
   * Zajęcia dojazdowe nie zajmują sali — nauczyciel jedzie do ucznia.
   *
   * Pole sali wtedy znika zamiast pokazywać się jako nieaktywne: nie ma tu
   * decyzji do podjęcia, a pusta kontrolka tylko każe się zastanawiać, czy
   * czegoś się nie zapomniało.
   */
  const dojazd = Boolean(wybranyUczen?.is_traveling)

  /**
   * Instrumenty do wyboru w tym formularzu.
   *
   * Przy wskazanym uczniu zawężamy listę do jego instrumentów — nie ma sensu
   * przypisywać lekcji fortepianu komuś, kto gra na skrzypcach. Zajęcia grupowe
   * (bez ucznia) korzystają z pełnego słownika.
   */
  const instrumentyDoWyboru = wybranyUczen
    ? wybranyUczen.instruments
    : (instrumentsQuery.data ?? [])

  /**
   * Czy jest w ogóle z czego wybierać.
   *
   * Jeden instrument ucznia podstawia się sam, brak instrumentów nie daje
   * czego wskazać — w obu przypadkach pole byłoby kontrolką bez decyzji.
   */
  const wyborInstrumentu =
    instrumentyDoWyboru.length > 1 || (!wybranyUczen && instrumentyDoWyboru.length > 0)

  // Jeden instrument = brak decyzji do podjęcia. Podstawiamy go sami, a pole
  // znika z formularza; przy kilku wybór zostaje przy człowieku.
  useEffect(() => {
    if (!form) return
    if (!wybranyUczen) return

    if (wybranyUczen.instruments.length === 1) {
      const jedyny = wybranyUczen.instruments[0].id
      if (form.instrumentId !== jedyny) update('instrumentId', jedyny)
      return
    }
    // Zmiana ucznia mogła unieważnić wcześniejszy wybór.
    if (form.instrumentId && !wybranyUczen.instruments.some((i) => i.id === form.instrumentId)) {
      update('instrumentId', '')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wybranyUczen?.id, form?.studentId])

  const mutation = useMutation({
    mutationFn: async () => {
      if (!form) throw new Error('Formularz nie jest gotowy.')
      const day = new Date(`${form.date}T00:00:00`)

      const base = {
        room_id: dojazd ? null : form.roomId,
        teacher_id: teacherMode ? (user?.id ?? '') : form.teacherId,
        student_id: form.studentId || null,
        instrument_id: form.instrumentId || null,
        duration_minutes: Number(form.duration),
        title: form.title.trim() || null,
        notes: form.notes.trim() || null,
      }

      if (seriesId) {
        return {
          kind: 'series' as const,
          data: await scheduleApi.updateSeries(seriesId, {
            room_id: base.room_id,
            teacher_id: base.teacher_id,
            instrument_id: base.instrument_id,
            duration_minutes: base.duration_minutes,
            title: base.title,
            notes: base.notes,
            weekday: form.weekday,
            start_time: `${form.time}:00`,
            ends_on: form.endsOn,
          }),
        }
      }

      if (makeupFor) {
        return {
          kind: 'lesson' as const,
          data: await scheduleApi.createLesson({
            ...base,
            starts_at: combineToIso(day, form.time),
            makeup_for_lesson_id: makeupFor.id,
          }),
        }
      }

      if (isEdit && lesson) {
        return {
          kind: 'lesson' as const,
          data: await scheduleApi.updateLesson(lesson.id, {
            ...base,
            starts_at: combineToIso(day, form.time),
          }),
        }
      }

      if (form.recurring) {
        return {
          kind: 'series' as const,
          data: await scheduleApi.createSeries({
            ...base,
            weekday: form.weekday,
            start_time: `${form.time}:00`,
            starts_on: form.date,
            ends_on: form.endsOn,
          }),
        }
      }

      return {
        kind: 'lesson' as const,
        data: await scheduleApi.createLesson({
          ...base,
          starts_at: combineToIso(day, form.time),
        }),
      }
    },
    onSuccess: async (result) => {
      // Podpowiedź na następny raz. Dopiero po udanym zapisie — sala odrzucona
      // z powodu kolizji nie jest wyborem, tylko nieudaną próbą.
      if (!dojazd) zapamietajSale(locationId, form?.roomId ?? '')

      await queryClient.invalidateQueries({ queryKey: ['schedule'] })
      // Odrobienie zdejmuje pozycję z listy „do odrobienia” — bez tego
      // wisiałaby tam do przeładowania strony.
      if (isMakeup) await queryClient.invalidateQueries({ queryKey: ['attendance'] })

      if (result.kind === 'series') {
        const { created_count: created, skipped: pominiete } = result.data
        if (pominiete.length > 0) {
          // Nie zamykamy okna — użytkownik musi zobaczyć, których terminów
          // nie udało się zająć, żeby rozwiązać je ręcznie.
          setSkipped(pominiete)
          notifySuccess(`Utworzono ${created} zajęć. Część terminów wymaga uwagi.`)
          return
        }
        notifySuccess(
          isSeriesEdit
            ? `Plan zmieniony — przebudowano ${created} przyszłych terminów.`
            : `Utworzono ${created} zajęć z planu tygodniowego.`,
        )
      } else {
        notifySuccess(isEdit ? 'Zapisano zmiany.' : 'Zajęcia zostały dodane do grafiku.')
      }
      onClose()
    },
    onError: (caught: unknown) => {
      if (caught instanceof ApiError) {
        setError(caught.message)
        setFieldErrors(caught.fieldErrors)
      } else {
        setError('Nie udało się zapisać zajęć.')
      }
    },
  })

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault()
    setError(null)
    setFieldErrors({})
    setSkipped([])
    mutation.mutate()
  }

  if (!form) {
    // Przy cyklu formularz czeka na jego ustawienia — wypełnienie go domyślnymi
    // wartościami nadpisałoby plan przy pierwszym zapisie. Okno musi się jednak
    // pokazać: kliknięcie, po którym przez chwilę nic się nie dzieje, wygląda
    // jak zepsuty przycisk.
    if (!isSeriesEdit) return null
    return (
      <Modal open={open} onClose={onClose} title="Edytuj plan tygodniowy" size="lg">
        {seriesQuery.isError ? (
          <Alert tone="error">Nie udało się wczytać planu tygodniowego.</Alert>
        ) : (
          <div className="flex justify-center py-10">
            <Spinner className="h-8 w-8 text-primary" label="Wczytywanie planu…" />
          </div>
        )}
      </Modal>
    )
  }

  const canSubmit =
    (dojazd || form.roomId !== '') &&
    (teacherMode || form.teacherId !== '') &&
    (form.studentId !== '' || form.title.trim() !== '')

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={
        isSeriesEdit
          ? 'Edytuj plan tygodniowy'
          : isMakeup
            ? 'Termin odrobienia'
            : isEdit
              ? 'Edytuj zajęcia'
              : form.recurring
                ? 'Nowy plan tygodniowy'
                : 'Nowe zajęcia'
      }
      description={
        // Nauczyciel nie ma pola „Prowadzący” — nie ma tam czego wybierać.
        // Zamiast nieaktywnej kontrolki mówi mu to opis okna.
        isSeriesEdit
          ? 'Zmiana obejmie wyłącznie przyszłe terminy. Zajęcia, które się już odbyły, zostają bez zmian.'
          : isMakeup
            ? 'Zajęcia w miejsce zgłoszonej nieobecności. Wskaż nowy termin — reszta jest przepisana z opuszczonej lekcji.'
            : teacherMode
              ? 'Zajęcia zostaną zapisane na Ciebie jako prowadzącego.'
              : isEdit
                ? 'Zmiana godziny lub sali sprawdzi kolizje z pozostałym grafikiem.'
                : 'System nie pozwoli zarezerwować zajętej sali ani nauczyciela.'
      }
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {skipped.length > 0 ? 'Zamknij' : 'Anuluj'}
          </Button>
          <Button
            type="submit"
            form="lesson-form"
            isLoading={mutation.isPending}
            disabled={!canSubmit}
          >
            {isEdit || isSeriesEdit
              ? 'Zapisz zmiany'
              : isMakeup
                ? 'Zarezerwuj odrobienie'
                : form.recurring
                  ? 'Utwórz plan'
                  : 'Dodaj zajęcia'}
          </Button>
        </>
      }
    >
      <form id="lesson-form" onSubmit={handleSubmit} className="space-y-5" noValidate>
        {error && <Alert tone="error">{error}</Alert>}

        {skipped.length > 0 && (
          <Alert tone="warning" title={`Pominięto ${skipped.length} terminów`}>
            <ul className="mt-1 space-y-1">
              {skipped.map((item) => (
                <li key={item.starts_at}>
                  {new Date(item.starts_at).toLocaleString('pl-PL', {
                    dateStyle: 'short',
                    timeStyle: 'short',
                  })}{' '}
                  — {item.reason}
                </li>
              ))}
            </ul>
            Pozostałe zajęcia zostały zapisane. Te terminy ustaw ręcznie w innej sali lub o innej
            godzinie.
          </Alert>
        )}

        {makeupFor && (
          <Alert tone="info" title="Odrobienie nieobecności">
            {makeupFor.student?.full_name ?? makeupFor.display_title} — opuszczone zajęcia z{' '}
            {new Date(makeupFor.starts_at).toLocaleString('pl-PL', {
              dateStyle: 'long',
              timeStyle: 'short',
            })}
            . Po zapisaniu znikną z listy „do odrobienia”.
          </Alert>
        )}

        <div className="grid gap-5 sm:grid-cols-2">
          {/* Ucznia w istniejącym cyklu nie zmieniamy: to byłby inny plan,
              a jego dotychczasowe terminy zostają w historii pierwszego. */}
          <SelectField
            label="Uczeń"
            value={form.studentId}
            onChange={(event) => update('studentId', event.target.value)}
            options={uczniowie.map((item) => ({
              value: item.id,
              label: `${item.last_name} ${item.first_name}`,
            }))}
            placeholder="Zajęcia grupowe (bez ucznia)"
            hint={
              isSeriesEdit
                ? 'Ucznia nie da się zmienić w istniejącym planie — załóż nowy.'
                : isMakeup
                  ? 'Odrobienie dotyczy tego samego ucznia co opuszczone zajęcia.'
                  : 'Przy zajęciach grupowych zostaw puste i podaj tytuł.'
            }
            disabled={isSeriesEdit || isMakeup}
            error={fieldErrors.student_id}
          />

          {/* Pole pojawia się wyłącznie wtedy, gdy jest z czego wybierać.
              Uczeń z jednym instrumentem dostaje go automatycznie, uczeń bez
              instrumentów nie ma czego wskazać — w obu przypadkach kontrolka
              byłaby martwa. Co zostało podstawione, widać w szczegółach zajęć. */}
          {wyborInstrumentu && (
            <SelectField
              label="Instrument"
              value={form.instrumentId}
              onChange={(event) => update('instrumentId', event.target.value)}
              options={instrumentyDoWyboru.map((item) => ({
                value: item.id,
                label: item.name,
              }))}
              placeholder="Nie wybrano"
              hint={
                wybranyUczen ? 'Uczeń gra na kilku — wskaż, którego dotyczą zajęcia.' : undefined
              }
              error={fieldErrors.instrument_id}
            />
          )}
        </div>

        {/* Pod wierszem ucznia, bo to jego wybór odsłania ten komunikat —
            i tłumaczy on, czemu niżej nie ma pola sali. */}
        {dojazd && (
          <Alert tone="info">
            {wybranyUczen?.full_name} ma zajęcia z dojazdem — odbywają się pod jego adresem, więc
            nie zajmują sali i nie pojawią się w grafiku sal.
          </Alert>
        )}

        <div className="grid gap-5 sm:grid-cols-2">
          {!dojazd && (
            <SelectField
              label="Sala"
              value={form.roomId}
              onChange={(event) => update('roomId', event.target.value)}
              options={rooms.map((room) => ({ value: room.id, label: room.name }))}
              placeholder={roomsQuery.isPending ? 'Wczytywanie…' : 'Wybierz salę'}
              error={fieldErrors.room_id}
              required
            />
          )}

          {/* Nauczyciel nie widzi tego pola wcale. Wcześniej stało tu jako
              nieaktywne z własnym nazwiskiem — kontrolka, której nie da się
              użyć, każe się zastanawiać, czy czegoś się nie przeoczyło.
              Informację niesie opis okna. */}
          {!teacherMode && (
            <SelectField
              label="Prowadzący"
              value={form.teacherId}
              onChange={(event) => update('teacherId', event.target.value)}
              options={(teachersQuery.data?.items ?? []).map((item) => ({
                value: item.user.id,
                label: item.user.full_name,
              }))}
              placeholder="Wybierz nauczyciela"
              error={fieldErrors.teacher_id}
              required
            />
          )}
        </div>

        <TextField
          label={form.studentId ? 'Tytuł (opcjonalnie)' : 'Tytuł zajęć'}
          value={form.title}
          onChange={(event) => update('title', event.target.value)}
          placeholder="np. Warsztaty improwizacji"
          hint={
            form.studentId
              ? 'Puste pole oznacza, że w grafiku pojawi się nazwisko ucznia.'
              : 'Wymagany, gdy zajęcia nie mają przypisanego ucznia.'
          }
          error={fieldErrors.title}
        />

        <div className="grid gap-5 sm:grid-cols-3">
          {isSeriesEdit ? (
            // W istniejącym planie nie ma „pierwszego terminu” do zmiany —
            // jest dzień tygodnia, w którym zajęcia mają się odtąd odbywać.
            <SelectField
              label="Dzień tygodnia"
              value={form.weekday}
              onChange={(event) => update('weekday', event.target.value as Weekday)}
              options={WEEKDAYS.map((item) => ({ value: item, label: WEEKDAY_LABELS[item] }))}
              error={fieldErrors.weekday}
              required
            />
          ) : (
            <TextField
              label={form.recurring ? 'Pierwszy termin' : 'Data'}
              type="date"
              value={form.date}
              onChange={(event) => {
                update('date', event.target.value)
                if (event.target.value) {
                  update('weekday', weekdayOf(new Date(`${event.target.value}T00:00:00`)))
                }
              }}
              error={fieldErrors.starts_at}
              required
            />
          )}
          <TimeSelect
            label="Godzina"
            value={form.time}
            onChange={(value) => update('time', value)}
            required
          />
          <SelectField
            label="Czas trwania"
            value={form.duration}
            onChange={(event) => update('duration', event.target.value)}
            options={CZASY}
            error={fieldErrors.duration_minutes}
          />
        </div>

        {isSeriesEdit && (
          <div className="grid gap-5 sm:grid-cols-2">
            <TextField
              label="Powtarzaj do"
              type="date"
              hint={`Zajęcia powtarzają się ${WEEKDAY_EVERY[form.weekday]}.`}
              value={form.endsOn}
              min={form.date}
              onChange={(event) => update('endsOn', event.target.value)}
              error={fieldErrors.ends_on}
              required
            />
          </div>
        )}

        {!isEdit && !isSeriesEdit && !isMakeup && (
          <div className="space-y-4 rounded-lg border border-outline/20 p-4">
            <label className="flex cursor-pointer items-start gap-3">
              <input
                type="checkbox"
                checked={form.recurring}
                onChange={(event) => update('recurring', event.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-outline/40 text-primary focus:ring-primary"
              />
              <span>
                <span className="block text-sm font-bold text-on-surface">
                  Powtarzaj co tydzień
                </span>
                <span className="block text-xs text-on-surface-variant">
                  Stały plan zajęć — system wygeneruje wszystkie terminy w okresie.
                </span>
              </span>
            </label>

            {form.recurring && (
              <div className="grid gap-5 sm:grid-cols-2">
                {/* Dzień tygodnia wynika z daty pierwszego terminu, więc nie
                    stoi tu jako osobne, nieaktywne pole — mówi go podpowiedź
                    pod datą zakończenia. */}
                <TextField
                  label="Powtarzaj do"
                  type="date"
                  hint={`Zajęcia będą się powtarzać ${WEEKDAY_EVERY[form.weekday]}.`}
                  value={form.endsOn}
                  min={form.date}
                  onChange={(event) => update('endsOn', event.target.value)}
                  error={fieldErrors.ends_on}
                  required
                />
              </div>
            )}
          </div>
        )}

        <div className="space-y-1.5">
          <label htmlFor="lesson-notes" className="block text-sm font-semibold">
            Notatka
          </label>
          <textarea
            id="lesson-notes"
            value={form.notes}
            onChange={(event) => update('notes', event.target.value)}
            rows={2}
            maxLength={2000}
            className="w-full rounded-lg border-2 border-transparent bg-surface-container-low px-4 py-3 text-sm transition-colors focus:border-primary focus:bg-surface focus:outline-none"
          />
        </div>
      </form>
    </Modal>
  )
}
